import React, { useMemo } from 'react';
import { Card, Row, Col, Statistic, Typography, Table, Divider, Empty, Tag, Alert, Progress } from 'antd';
import {
  DownloadOutlined,
  FileExcelOutlined,
  TeamOutlined,
  AppstoreOutlined,
  ScissorOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { TAIPEI_TZ } from '../utils/datetime';

const { Text, Title } = Typography;

// 卡片等高 + 副標固定一行，避免有無副標造成高度參差（與 Alert 頁一致）
const CARD_STYLE: React.CSSProperties = { width: '100%' };
const SUB_STYLE: React.CSSProperties = {
  fontSize: 12,
  display: 'block',
  marginTop: 4,
  minHeight: 18,
};

// 後端寫死的單次匯出上限；超過就只送前 10000 列
const ROW_CAP = 10000;

// created_time / last_use 是 unix「秒」（ExportTask 與 AlertRecord 的毫秒不同）
const fmtSec = (sec: number, withTime = false): string => {
  if (!sec) return '—';
  return dayjs.unix(sec).tz(TAIPEI_TZ).format(withTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD');
};

// 單筆匯出明細（展開列用）
interface ExportTaskDetail {
  task_id: string;
  type: string;
  keyword: string;
  keyword_truncated: boolean;
  channel: string; // "FB,IG,YT"
  nation: string;
  st: string; // unix 秒字串
  et: string;
  num_found: number; // 查詢匹配到的篇數
  num_fetched: number; // 實際匯出的篇數
  rows_lost: number; // 因上限沒送出的篇數
  task_status: string; // done / failed / processing
  created_time: number;
}

// 後端 /api/trend-requests/export-usage 每列（一個客戶 service_id）
interface ExportUsageRow {
  service_id: string;
  org_name: string;
  tasks: number;
  rows_exported: number;
  rows_found: number;
  rows_lost: number;
  truncated_tasks: number;
  capped_tasks: number;
  failed_tasks: number;
  last_use: number;
  export_tasks: ExportTaskDetail[];
}

interface ExportAnalysisProps {
  data: {
    data?: Array<Record<string, any>>;
    time_range?: { start_date?: string; end_date?: string };
    eligible_services?: number; // 有匯出權限的有效客戶數（分母）
  } | null;
}

const ExportAnalysis: React.FC<ExportAnalysisProps> = ({ data }) => {
  const rows: ExportUsageRow[] = useMemo(
    () => (Array.isArray(data?.data) ? (data!.data as unknown as ExportUsageRow[]) : []),
    [data]
  );

  const eligible = data?.eligible_services ?? 0;

  const totals = useMemo(() => {
    const tasks = rows.reduce((s, r) => s + r.tasks, 0);
    return {
      tasks,
      rowsExported: rows.reduce((s, r) => s + r.rows_exported, 0),
      rowsFound: rows.reduce((s, r) => s + r.rows_found, 0),
      rowsLost: rows.reduce((s, r) => s + r.rows_lost, 0),
      cappedTasks: rows.reduce((s, r) => s + r.capped_tasks, 0),
      failedTasks: rows.reduce((s, r) => s + r.failed_tasks, 0),
      // 期間內曾被上限截斷過的客戶數
      cappedCustomers: rows.filter((r) => r.capped_tasks > 0).length,
    };
  }, [rows]);

  const pctOf = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

  // 留言匯出機制不同，這裡先固定標示；等資料源整合後再併入本頁
  const commentExportNotice = (
    <Alert
      type="warning"
      showIcon
      icon={<WarningOutlined />}
      style={{ marginBottom: 16 }}
      message="本頁只涵蓋「文章匯出」；留言匯出機制不同，待整合開發"
      description={
        <>
          文章匯出會寫入 <Text code>trend.ExportTask</Text>，有 2024-07 起的完整歷史，因此可以在這裡分析。
          留言匯出（權限 <Text code>exportcomments</Text>）走另一套流程：直接產生 Google Sheet 並寄信，
          <Text strong>不寫任何資料表</Text>，用量只存在 Cloud Logging 且保留期僅 30 天，過期即永久消失。
          圖表匯出（<Text code>export-excel-api</Text>）狀況相同。兩者都需要另外建立 log sink 匯入 BigQuery
          才有長期資料，屬待整合開發項目。
        </>
      }
    />
  );

  if (!rows.length) {
    return (
      <>
        {commentExportNotice}
        <Empty description="此區間沒有文章匯出紀錄" style={{ padding: '60px 0' }} />
      </>
    );
  }

  // 展開列：該客戶在區間內的每一筆匯出
  const renderTaskDetails = (tasks: ExportTaskDetail[]) => {
    const detailColumns: ColumnsType<ExportTaskDetail> = [
      {
        title: '匯出時間',
        dataIndex: 'created_time',
        key: 'created_time',
        width: 140,
        defaultSortOrder: 'descend',
        sorter: (a, b) => a.created_time - b.created_time,
        render: (v: number) => fmtSec(v, true),
      },
      {
        title: '狀態',
        dataIndex: 'task_status',
        key: 'task_status',
        width: 90,
        filters: [
          { text: '完成', value: 'done' },
          { text: '失敗', value: 'failed' },
          { text: '處理中', value: 'processing' },
        ],
        onFilter: (value, record) => record.task_status === value,
        render: (v: string) => {
          if (v === 'done') return <Tag color="green">完成</Tag>;
          if (v === 'failed') return <Tag color="red">失敗</Tag>;
          return <Tag color="orange">{v || '—'}</Tag>;
        },
      },
      {
        title: '查詢關鍵字',
        dataIndex: 'keyword',
        key: 'keyword',
        render: (v: string, r) =>
          v ? (
            <Text code copyable={{ text: v }}>
              {v}
              {r.keyword_truncated && <Text type="secondary">…（過長已截斷顯示）</Text>}
            </Text>
          ) : (
            <Text type="secondary">（無）</Text>
          ),
      },
      {
        title: '頻道',
        dataIndex: 'channel',
        key: 'channel',
        width: 200,
        render: (v: string) =>
          v ? v.split(',').map((ch) => <Tag key={ch} style={{ marginBottom: 2 }}>{ch}</Tag>) : '—',
      },
      { title: '國別', dataIndex: 'nation', key: 'nation', width: 70 },
      {
        title: '查詢區間',
        key: 'range',
        width: 190,
        render: (_: unknown, r) => {
          const st = Number(r.st);
          const et = Number(r.et);
          if (!st || !et) return '—';
          const days = Math.max(Math.round((et - st) / 86400), 0);
          return (
            <span>
              {fmtSec(st)} ~ {fmtSec(et)}{' '}
              <Text type="secondary" style={{ fontSize: 12 }}>({days} 天)</Text>
            </span>
          );
        },
      },
      {
        title: '匯出 / 匹配篇數',
        key: 'rows',
        width: 170,
        sorter: (a, b) => a.num_fetched - b.num_fetched,
        render: (_: unknown, r) => (
          <span>
            {r.num_fetched.toLocaleString()}
            <Text type="secondary"> / {r.num_found.toLocaleString()}</Text>
            {r.rows_lost > 0 && (
              <Tag color="orange" style={{ marginLeft: 6 }}>
                少 {r.rows_lost.toLocaleString()}
              </Tag>
            )}
          </span>
        ),
      },
    ];
    return (
      <Table<ExportTaskDetail>
        dataSource={tasks}
        columns={detailColumns}
        rowKey="task_id"
        size="small"
        pagination={tasks.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
      />
    );
  };

  const columns: ColumnsType<ExportUsageRow> = [
    {
      title: '客戶',
      dataIndex: 'org_name',
      key: 'org_name',
      fixed: 'left',
      width: 240,
      render: (v: string, r) => (
        <span>
          {v} <Text type="secondary" style={{ fontSize: 12 }}>({r.service_id})</Text>
        </span>
      ),
    },
    {
      title: '匯出次數',
      dataIndex: 'tasks',
      key: 'tasks',
      width: 100,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.tasks - b.tasks,
    },
    {
      title: '匯出篇數',
      dataIndex: 'rows_exported',
      key: 'rows_exported',
      width: 120,
      sorter: (a, b) => a.rows_exported - b.rows_exported,
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: `卡 ${ROW_CAP.toLocaleString()} 篇上限`,
      dataIndex: 'capped_tasks',
      key: 'capped_tasks',
      width: 130,
      sorter: (a, b) => a.capped_tasks - b.capped_tasks,
      render: (v: number, r) =>
        v > 0 ? (
          <span>
            <Tag color="orange">{v} 次</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>{pctOf(v, r.tasks)}%</Text>
          </span>
        ) : (
          <Text type="secondary">0</Text>
        ),
    },
    {
      title: '未送出篇數',
      dataIndex: 'rows_lost',
      key: 'rows_lost',
      width: 120,
      sorter: (a, b) => a.rows_lost - b.rows_lost,
      render: (v: number) =>
        v > 0 ? <Text type="warning">{v.toLocaleString()}</Text> : <Text type="secondary">0</Text>,
    },
    {
      title: '失敗次數',
      dataIndex: 'failed_tasks',
      key: 'failed_tasks',
      width: 100,
      sorter: (a, b) => a.failed_tasks - b.failed_tasks,
      render: (v: number) => (v > 0 ? <Text type="danger">{v}</Text> : <Text type="secondary">0</Text>),
    },
    {
      title: '最後匯出',
      dataIndex: 'last_use',
      key: 'last_use',
      width: 130,
      sorter: (a, b) => a.last_use - b.last_use,
      render: (v: number) => fmtSec(v),
    },
  ];

  return (
    <>
      {data?.time_range && (
        <Text type="secondary">
          匯出統計區間：{data.time_range.start_date} ~ {data.time_range.end_date || '今天'}
          （僅列出目前有效且具匯出權限的客戶）
        </Text>
      )}
      <Divider />

      {/* 總覽（每張卡固定等高，副標行一律保留以對齊） */}
      <Row gutter={16} style={{ marginBottom: 24 }} align="stretch">
        <Col xs={12} md={5} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><TeamOutlined /> 期間使用客戶</span>}
              value={rows.length}
              suffix={`/ ${eligible}`}
            />
            <Text type="secondary" style={SUB_STYLE}>佔有權限客戶 {pctOf(rows.length, eligible)}%</Text>
          </Card>
        </Col>
        <Col xs={12} md={5} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title={<span><DownloadOutlined /> 匯出次數</span>} value={totals.tasks} />
            <Text type="secondary" style={SUB_STYLE}>
              {totals.failedTasks > 0 ? `其中失敗 ${totals.failedTasks} 次` : '無失敗紀錄'}
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={5} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><FileExcelOutlined /> 匯出篇數</span>}
              value={totals.rowsExported}
            />
            <Text type="secondary" style={SUB_STYLE}>
              平均每次 {totals.tasks ? Math.round(totals.rowsExported / totals.tasks).toLocaleString() : 0} 篇
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={5} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><ScissorOutlined /> 卡上限次數</span>}
              value={totals.cappedTasks}
              suffix={`/ ${totals.tasks}`}
            />
            <Text type="secondary" style={SUB_STYLE}>佔匯出次數 {pctOf(totals.cappedTasks, totals.tasks)}%</Text>
          </Card>
        </Col>
        <Col xs={12} md={4} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><WarningOutlined /> 未送出篇數</span>}
              value={totals.rowsLost}
              valueStyle={totals.rowsLost > 0 ? { color: '#d46b08' } : undefined}
            />
            <Text type="secondary" style={SUB_STYLE}>佔匹配總數 {pctOf(totals.rowsLost, totals.rowsFound)}%</Text>
          </Card>
        </Col>
      </Row>

      {/* 採用漏斗 */}
      <Card title={<span><AppstoreOutlined /> 匯出採用情況</span>} style={{ marginBottom: 24 }}>
        <Row gutter={16} align="stretch">
          <Col xs={24} md={8} style={{ display: 'flex' }}>
            <Card size="small" style={CARD_STYLE}>
              <Statistic title="可用匯出的客戶（有權限）" value={eligible} suffix="家" />
              <Progress percent={100} size="small" showInfo={false} />
              <Text type="secondary" style={SUB_STYLE}>分母：customization 含 download_excel_png</Text>
            </Card>
          </Col>
          <Col xs={24} md={8} style={{ display: 'flex' }}>
            <Card size="small" style={CARD_STYLE}>
              <Statistic title="期間實際使用的客戶" value={rows.length} suffix="家" />
              <Progress percent={pctOf(rows.length, eligible)} size="small" showInfo={false} />
              <Text type="secondary" style={SUB_STYLE}>
                佔有權限客戶 {pctOf(rows.length, eligible)}%（{Math.max(eligible - rows.length, 0)} 家未使用）
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8} style={{ display: 'flex' }}>
            <Card size="small" style={CARD_STYLE}>
              <Statistic
                title="曾被篇數上限截斷的客戶"
                value={totals.cappedCustomers}
                suffix={`/ ${rows.length}`}
              />
              <Progress
                percent={pctOf(totals.cappedCustomers, rows.length)}
                size="small"
                showInfo={false}
                strokeColor="#fa8c16"
              />
              <Text type="secondary" style={SUB_STYLE}>
                佔使用客戶 {pctOf(totals.cappedCustomers, rows.length)}%
              </Text>
            </Card>
          </Col>
        </Row>
      </Card>

      {commentExportNotice}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`單次匯出上限 ${ROW_CAP.toLocaleString()} 篇：「匹配篇數」是查詢實際命中的量，超出部分不會出現在檔案裡`}
        description="「未送出篇數」= 匹配篇數 − 匯出篇數，代表客戶想要卻沒拿到的資料量。已停用或已到期的客戶不列入本頁。"
      />

      {/* 每客戶明細（點展開看個別匯出任務與查詢關鍵字） */}
      <Card title={<Title level={5} style={{ margin: 0 }}>各客戶匯出使用（點客戶列展開個別匯出紀錄）</Title>}>
        <Table<ExportUsageRow>
          dataSource={rows}
          columns={columns}
          rowKey="service_id"
          size="small"
          scroll={{ x: 1140 }}
          pagination={{ pageSize: 15, showSizeChanger: true }}
          expandable={{
            rowExpandable: (r) => r.export_tasks.length > 0,
            expandedRowRender: (r) => renderTaskDetails(r.export_tasks),
          }}
        />
      </Card>
    </>
  );
};

export default ExportAnalysis;
